"use client"

import type { JobAdDetail } from "@/modules/jobAdDetail/types"
import type { FunctionComponent } from "react"

import classNames from "classnames"
import { useRouter } from "next/navigation"

import { IconButton } from "@jobteaser/spark/components/IconButton"
import { HeartFilled } from "@jobteaser/spark/components/Icons/HeartFilled"
import { HeartOutlined } from "@jobteaser/spark/components/Icons/HeartOutlined"

import { usePageLayoutContext } from "@/modules/jobAdDetail/components/PageLayout/PageLayoutContext"
import { useJobAdDetailTracking } from "@/modules/jobAdDetail/hooks/useJobAdDetailTracking"
import { toggleJobAdSavedStatus } from "@/modules/jobAdDetail/services/toggleJobAdSavedStatus"
import { useNotification } from "@/modules/notification/hooks"

import styles from "./SaveButton.module.css"

type SaveButtonProps = {
  errorContent: string
  errorTitle: string
  hasApplied: boolean
  isJobAdSaved: boolean
  isLogged: boolean
  jobAdId: JobAdDetail["id"]
  jobAdLocale: JobAdDetail["languageCode"]
  saveLabel: string
  unSaveLabel: string
}

export const SaveButton: FunctionComponent<SaveButtonProps> = ({
  errorContent,
  errorTitle,
  hasApplied,
  isJobAdSaved,
  isLogged,
  jobAdId,
  jobAdLocale,
  saveLabel,
  unSaveLabel,
}) => {
  const { openLoginFlow } = usePageLayoutContext()
  const { refresh } = useRouter()
  const showNotification = useNotification()
  const { trackJobAdDetailSaveEvent } = useJobAdDetailTracking()

  const handleSave = (): void => {
    trackJobAdDetailSaveEvent(!isJobAdSaved)

    if (!isLogged) {
      openLoginFlow("saveJobAd")
      return
    }

    toggleJobAdSavedStatus({
      hostname: window.location.host,
      isJobAdSaved: !isJobAdSaved,
      jobAdId,
      jobAdLocale,
    })
      .then(() => {
        refresh()
      })
      .catch(() => {
        showNotification({
          content: errorContent,
          title: errorTitle,
          variant: "error",
        })
      })
  }

  return (
    <IconButton
      aria-label={isJobAdSaved ? unSaveLabel : saveLabel}
      className={classNames(styles.main, { [styles.active]: isJobAdSaved })}
      data-testid={
        isJobAdSaved ? "jobad-DetailView__CandidateActions__saved" : "jobad-DetailView__CandidateActions__save"
      }
      icon={isJobAdSaved ? HeartFilled : HeartOutlined}
      onClick={handleSave}
      variant="outlined"
      disabled={hasApplied}
    />
  )
}
